import React, { useEffect, useMemo, useRef } from 'react';
import { buildAttentionModel, heatColor, mapKeywords, summarizePlacements, layoutPins, ZONES } from '../gaze/gazeModel';

/**
 * Attention-heatmap overlay for the PDF canvas preview (content-aware v2).
 * The heat field follows this page's actual layout, name block, section tops,
 * the most recent role, dates, on top of the F-pattern base. Keywords are
 * marked with numbered pins (collision-free) that match the summary chips.
 *
 * Props:
 *   width, height , CSS px of the rendered page
 *   tokens        , [{str,x,y,w,h}] text geometry for THIS page (CSS px)
 *   pageIndex     , 0-based page number (later pages are discounted)
 *   keywords      , JD must-have keywords to map
 *   onStats(summary), optional; receives {hot,warm,cold,notFound}
 */
export default function GazeOverlay({ width, height, tokens, pageIndex = 0, keywords = [], onStats }) {
  const heatRef = useRef(null);

  const model = useMemo(
    () => buildAttentionModel({ width, height, pageIndex, tokens: tokens || [] }),
    [tokens, width, height, pageIndex]
  );

  const placements = useMemo(
    () => mapKeywords(tokens || [], keywords, model.weightAt),
    [tokens, keywords, model]
  );

  const pins = useMemo(() => layoutPins(placements), [placements]);

  useEffect(() => {
    if (onStats) {
      onStats({
        ...summarizePlacements(placements),
        pins: pins.map(p => ({ keyword: p.keyword, index: p.index })),
      });
    }
  }, [placements, pins, onStats]);

  // Paint the combined attention field at coarse resolution; CSS blur smooths it.
  useEffect(() => {
    const canvas = heatRef.current;
    if (!canvas || !width || !height) return;
    const CELL = 12;
    canvas.width = Math.ceil(width / CELL);
    canvas.height = Math.ceil(height / CELL);
    const ctx = canvas.getContext('2d');
    for (let gy = 0; gy < canvas.height; gy++) {
      for (let gx = 0; gx < canvas.width; gx++) {
        const w = model.weightAt((gx + 0.5) * CELL, (gy + 0.5) * CELL);
        ctx.fillStyle = heatColor(w);
        ctx.fillRect(gx, gy, 1, 1);
      }
    }
  }, [model, width, height]);

  if (!width || !height) return null;

  const zoneColor = zone => (ZONES[zone] ? ZONES[zone].color : '#2563eb');

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ width, height }}>
      <canvas
        ref={heatRef}
        className="absolute inset-0"
        style={{ width: '100%', height: '100%', filter: 'blur(10px)', opacity: 0.85 }}
      />
      {pins.map(p => (
        <div key={`${p.keyword}-${p.index}`}>
          {/* underline the actual occurrence */}
          <div
            className="absolute rounded-sm"
            style={{
              left: p.x - 1,
              top: p.y + p.h,
              width: p.w + 2,
              height: 2.5,
              background: zoneColor(p.zone),
            }}
          />
          {/* numbered pin, collision-free */}
          <div
            className="absolute flex items-center justify-center rounded-full text-[10px] font-bold text-white shadow"
            title={p.keyword}
            style={{
              left: p.pinX - 9,
              top: p.pinY - 9,
              width: 18,
              height: 18,
              background: zoneColor(p.zone),
              border: '1.5px solid #fff',
            }}
          >
            {p.index}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Shared legend + summary panel for any gaze surface. Chips are numbered to
 *  match the pins on the page. */
export function GazeSummary({ stats }) {
  if (!stats) return null;
  const numberFor = kw => {
    const hit = (stats.pins || []).find(p => p.keyword === kw);
    return hit ? hit.index : null;
  };
  const rows = [
    { key: 'hot', label: '🔥 Seen in the first pass, these land inside the 7-second skim', cls: 'text-orange-700 bg-orange-50 border-orange-200' },
    { key: 'warm', label: '👀 Gets a glance, visible if the first pass earns a second look', cls: 'text-amber-700 bg-amber-50 border-amber-200' },
    { key: 'cold', label: '🧊 Effectively invisible, move these into your summary, skills, or your latest role’s first bullets', cls: 'text-blue-700 bg-blue-50 border-blue-200' },
    { key: 'notFound', label: '✗ Not found on this page', cls: 'text-gray-500 bg-gray-50 border-gray-200' },
  ];
  return (
    <div className="mt-3 space-y-2">
      {rows.map(({ key, label, cls }) =>
        stats[key] && stats[key].length > 0 ? (
          <div key={key} className={`border rounded-xl px-3 py-2 ${cls}`}>
            <p className="text-xs font-semibold mb-1">{label}</p>
            <div className="flex flex-wrap gap-1.5">
              {stats[key].map((kw, i) => {
                const n = numberFor(kw);
                return (
                  <span key={i} className="text-xs bg-white/70 border border-current/20 px-2 py-0.5 rounded-full">
                    {n ? <strong className="mr-1">{n}.</strong> : null}
                    {kw}
                  </span>
                );
              })}
            </div>
          </div>
        ) : null
      )}
      <p className="text-[11px] text-gray-400">
        Modeled on recruiter eye-tracking: a ~7-second first pass in an F-pattern, with fixations on
        the name, section tops, the most recent role, and dates. Hot zones get read; cold zones get
        skipped, placement matters as much as the words.
      </p>
    </div>
  );
}
